import { countryName, formatMoney } from '@/utils/format';
import { STAGES } from '@/utils/labels';

/**
 * The segment rule fields (SEG-1…3), as the editor shows them. The backend checks
 * the same fields, operators and values (backend/pkg/api/segments.go, segFields).
 */

export type SegmentKind = 'performance' | 'geography' | 'type' | 'lifecycle';

export const KINDS: SegmentKind[] = ['performance', 'geography', 'type', 'lifecycle'];

export const KIND_LABEL: Record<SegmentKind, string> = {
  performance: 'Performance',
  geography: 'Geography',
  type: 'Type',
  lifecycle: 'Lifecycle',
};

export type RuleField =
  | 'ggr_30d'
  | 'players_30d'
  | 'velocity'
  | 'rfm_recency'
  | 'rfm_frequency'
  | 'rfm_monetary'
  | 'country'
  | 'payments'
  | 'license'
  | 'stage'
  | 'has_casino';

export type Op = 'gte' | 'lte' | 'eq' | 'in' | 'not_in';

/** One condition as the API stores it. Several values (countries, stages) are comma-separated. */
export type ApiRule = { field: RuleField; op: Op; value: string };

/** A condition in the editor: the API rule plus a key for React. */
export type Rule = ApiRule & { id: string };

type FieldDef = {
  kind: SegmentKind;
  label: string;
  /** The label in a one-line summary of the rules. */
  short: string;
  ops: Op[];
  input: 'number' | 'choice';
  unit?: string;
  options?: { value: string; label: string }[];
  multi?: boolean;
  /** An open question about the rule, shown under it. */
  note?: string;
  /** No data for it yet: stored, but left out of the match count. */
  pending?: boolean;
  defaultValue: string;
};

const COUNTRY_CODES = ['ARG', 'BRA', 'CAN', 'CHL', 'COL', 'CZE', 'MEX', 'MLT', 'PER', 'ROU'];
// countryName knows Owner Hub's operating regions; two more appear in the demo data.
const EXTRA_COUNTRIES: Record<string, string> = { CAN: 'Canada', MLT: 'Malta' };
export const countryLabel = (code: string) => EXTRA_COUNTRIES[code] ?? countryName(code);

const Q12 = 'The metrics source has no player-level stats yet (Q12), so it is not counted yet.';

export const RULE_FIELDS: Record<RuleField, FieldDef> = {
  ggr_30d: { kind: 'performance', label: '30-day GGR', short: 'GGR 30d', ops: ['gte', 'lte'], input: 'number', unit: 'USD', defaultValue: '1000' },
  players_30d: { kind: 'performance', label: 'Players, last 30 days', short: 'Players 30d', ops: ['gte', 'lte', 'eq'], input: 'number', defaultValue: '1' },
  velocity: {
    kind: 'performance',
    label: 'Velocity score (0–100)',
    short: 'Velocity',
    ops: ['gte', 'lte'],
    input: 'number',
    note: 'A score from 0 to 100: Dormant below 20, High velocity from 60 (Q16).',
    defaultValue: '60',
  },
  rfm_recency: {
    kind: 'performance',
    label: 'Recency: days since a player last played',
    short: 'Last played',
    ops: ['lte', 'gte'],
    input: 'number',
    unit: 'days',
    note: Q12,
    pending: true,
    defaultValue: '7',
  },
  rfm_frequency: {
    kind: 'performance',
    label: 'Frequency: days played of the last 30',
    short: 'Days played',
    ops: ['gte', 'lte'],
    input: 'number',
    unit: 'days',
    note: Q12,
    pending: true,
    defaultValue: '10',
  },
  rfm_monetary: {
    kind: 'performance',
    label: 'Monetary: player deposits, last 30 days',
    short: 'Deposits 30d',
    ops: ['gte', 'lte'],
    input: 'number',
    unit: 'USD',
    note: Q12,
    pending: true,
    defaultValue: '5000',
  },
  country: {
    kind: 'geography',
    label: 'Country',
    short: 'Country',
    ops: ['in', 'not_in'],
    input: 'choice',
    multi: true,
    options: COUNTRY_CODES.map((c) => ({ value: c, label: countryLabel(c) })),
    note: 'The casino’s country, or the lead’s own.',
    defaultValue: 'BRA',
  },
  payments: {
    kind: 'type',
    label: 'Payments',
    short: 'Payments',
    ops: ['in'],
    input: 'choice',
    options: [
      { value: 'crypto', label: 'Takes crypto' },
      { value: 'fiat_only', label: 'Fiat only' },
    ],
    defaultValue: 'crypto',
  },
  license: {
    kind: 'type',
    label: 'License',
    short: 'License',
    ops: ['in'],
    input: 'choice',
    options: [
      { value: 'curacao', label: 'Curaçao' },
      { value: 'anjouan', label: 'Anjouan' },
      { value: 'malta', label: 'Malta' },
      { value: 'none', label: 'No license' },
    ],
    note: "The license of the casino's region in Owner Hub today: the deploy keeps only the licence text, not which license it was (Q5).",
    defaultValue: 'curacao',
  },
  stage: {
    kind: 'lifecycle',
    label: 'Lead stage',
    short: 'Stage',
    ops: ['in', 'not_in'],
    input: 'choice',
    multi: true,
    options: STAGES.map((s) => ({ value: s.value, label: s.label })),
    defaultValue: 'live',
  },
  has_casino: {
    kind: 'lifecycle',
    label: 'Has a casino',
    short: 'Has a casino',
    ops: ['in'],
    input: 'choice',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    defaultValue: 'yes',
  },
};

/**
 * The countries a Country condition offers, by name: each one that has its own segment
 * (every country a casino or lead is in), the ones the condition holds, and the usual
 * ones, so the team can group countries into regions in segments of its own.
 */
export const countryOptions = (segments: { country: string }[], picked: string[]) =>
  [...new Set([...COUNTRY_CODES, ...picked, ...segments.map((s) => s.country).filter(Boolean)])]
    .map((c) => ({ value: c, label: countryLabel(c) }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const FIELDS_BY_KIND = KINDS.map((kind) => ({
  kind,
  fields: (Object.keys(RULE_FIELDS) as RuleField[]).filter((f) => RULE_FIELDS[f].kind === kind),
}));

/** Operator wording in the editor. */
export const OP_WORD: Record<Op, string> = { gte: 'at least', lte: 'at most', eq: 'exactly', in: 'is', not_in: 'is not' };
const OP_SYMBOL: Record<Op, string> = { gte: '≥', lte: '≤', eq: '=', in: 'is', not_in: 'is not' };

let lastRuleId = 0;

/** A new condition on `field`, with its default operator and value. */
export const newRule = (field: RuleField, op?: Op, value?: string): Rule => {
  const def = RULE_FIELDS[field];
  lastRuleId += 1;
  return { id: `rule-${lastRuleId}`, field, op: op ?? def.ops[0], value: value ?? def.defaultValue };
};

/** The editor's conditions from the API's rules (unknown fields are dropped). */
export const fromApi = (rules: ApiRule[]): Rule[] =>
  rules.filter((r) => RULE_FIELDS[r.field]).map((r) => newRule(r.field, r.op, r.value));

/** The API's rules from the editor's conditions. */
export const toApi = (rules: Rule[]): ApiRule[] => rules.map(({ field, op, value }) => ({ field, op, value }));

/** One rule in a few words: "GGR 30d ≥ $1K", "Country is Brazil, Peru". */
export const describeRule = (r: ApiRule): string => {
  const d = RULE_FIELDS[r.field];
  if (!d) return r.field;
  if (d.input === 'number') {
    const n = Number(r.value) || 0;
    const value = d.unit === 'USD' ? formatMoney(n) : d.unit ? `${n} ${d.unit}` : String(n);
    return `${d.short} ${OP_SYMBOL[r.op]} ${value}`;
  }
  const names = r.value
    .split(',')
    .filter(Boolean)
    .map((v) => (r.field === 'country' ? countryLabel(v) : (d.options?.find((o) => o.value === v)?.label ?? v)));
  if (!d.multi) return `${d.short}: ${names[0] ?? '…'}`;
  return `${d.short} ${OP_SYMBOL[r.op]} ${names.join(', ') || '…'}`;
};

/** The kinds a segment's rules cover, for the list's "Kind" column. */
export const kindsOf = (rules: ApiRule[]): SegmentKind[] => KINDS.filter((k) => rules.some((r) => RULE_FIELDS[r.field]?.kind === k));
